import React, { ErrorInfo } from "react";
import { observer } from "mobx-react";
import { action, computed, observable } from "mobx";
import { Api } from "./api/Api";
import { Button } from "./views/Button";
import { addNotification } from "../AppNotification";
import { GlobalContext } from "../GlobalContext";
import { MainSidebar } from "../MainSidebar";
import ReactMarkdown from "react-markdown";
import { CircularProgress } from "@mui/material";
import { CopyButton } from "./views/CopyText";
import classNames from "classnames";
import { TopBarContent } from "./views/TopBarContent";
import { StepsLocalAPIStatusResult } from "./api/contracts/NoLocalApi";
import { isKnownNoLocalApiReason } from "./api/noLocalApi";
import { RouteComponentProps, withRouter } from "react-router";

const countTo = 5;

interface NoLocalApiPageProps {
  api: Api;
}

/**
 * Page shown when a user cannot access the local api. The page make various server requests to check the status of the
 * local api server (unavailable, starting...) and attempts to display an informative message and instructions on how
 * to resole issues. Most of the content is returned from the server.
 * */
@observer
class NoLocalApiPage extends React.Component<NoLocalApiPageProps & RouteComponentProps<{ reason?: string }>> {
  static contextType = GlobalContext;
  context!: React.ContextType<typeof GlobalContext>;
  _timer: any = null;
  _checking: boolean;

  _diagnosing: boolean = false;
  @observable
  _diagnosis?: StepsLocalAPIStatusResult = null;

  @observable
  _countDown: number = countTo;

  @computed
  get reason() {
    // react-router-v5 bug where match sometimes isn't updated
    return ((this.props as any).computedMatch ?? this.props.match).params.reason;
  }

  componentDidMount() {
    this._clear();
    this._startCheck();
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    this._clear();
    throw err;
  }

  componentWillUnmount() {
    this._clear();
  }

  _startCheck() {
    this._timer = setTimeout(() => this._check(), 1000);
  }

  _clear = () => {
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
  };

  private handleApiIsBack = () => {
    if (this.reason == null || isKnownNoLocalApiReason(this.reason)) {
      this.props.history.replace("/");
      window.location.reload();
    } else {
      this.props.history.goBack();
    }
  };

  @action
  _check() {
    this._countDown = this._countDown - 1;
    if (this._countDown === 0) {
      if (!this._checking) {
        this._checking = true;
        this.context.api
          .testLocalApi()
          .then(() => {
            this._clear();
            addNotification({
              title: "API is back",
              message: "Lets do some stuff!",
              level: "info",
            });
            this.handleApiIsBack();
          })
          .finally(() => {
            this._checking = false;
          });
      }

      if (!this._diagnosing) {
        this._diagnosing = true;
        this.context.api
          .diagnoseLocalApi(this.reason)
          .then(this._resolveResult)
          .then(
            action((diagnosis: StepsLocalAPIStatusResult) => {
              this._diagnosis = diagnosis;
            })
          )
          .finally(() => (this._diagnosing = false));
      }
    }
    if (this._countDown === -1) {
      this._countDown = countTo;
    }
    this._startCheck();
  }

  _resolveResult = (result: StepsLocalAPIStatusResult): Promise<StepsLocalAPIStatusResult> => {
    const localStepCheck = result.localCheck;
    if (localStepCheck) {
      const check = localStepCheck.check;
      if (check.clazz === "XHRCheck") {
        return new Promise<StepsLocalAPIStatusResult>((resolve) => {
          const onSuccess = () => {
            resolve(this._resolveResult(localStepCheck.onSuccess));
          };

          const image = new Image();
          image.onload = onSuccess;
          // The http connection timeout on browsers is much higher than 5 seconds by default,
          // That means that if a failure is sent, the user *can* connect to the server
          // but the server returns 400 for bad requests.
          image.onerror = onSuccess;
          image.src = localStepCheck.check.address;

          // Set timeout of 5 seconds to fail.
          setTimeout(() => {
            image.src = null;
            resolve(result);
          }, 5000);
        });
      } else {
        // Unknown check
        return Promise.resolve(result);
      }
    } else {
      return Promise.resolve(result);
    }
  };

  render() {
    // @ts-ignore
    return (
      <div className="container" style={{ marginLeft: this.context.globalUIStore.sidebarWidth }}>
        <MainSidebar showLinks={false} showImpersonate={this.context.auth?.user?.role === "master"} />
        <TopBarContent>
          <div className="template-title no-local-api-title">
            {this._diagnosis && (
              <ul>
                {this._diagnosis.stages.map((stage) => (
                  <li key={stage.name} className={`stage-${stage.clazz}`}>
                    <span className="icon" />
                    <span className="text">{stage.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TopBarContent>

        <div className="big-content center-action no-local-api">
          {this.context.api.impersonateOrganizationId && (
            <>
              <Button
                type="secondary"
                onClick={() => {
                  this.context.api.forceGlobalApi();
                }}
              >
                Use Global API
              </Button>

              <Button
                type="secondary"
                onClick={() => {
                  this.context.api.clearImpersonate();
                  this.handleApiIsBack();
                }}
              >
                Clear Impersonation
              </Button>
            </>
          )}

          <div className="content">
            <div className={classNames("illustration", "center-action", this._diagnosis?.stepName)} />
            <h1>
              {this._diagnosis?.title || <>Checking Private API Status</>} <CircularProgress size={18} />
            </h1>
            {this._diagnosis?.content ? (
              <ReactMarkdown components={{
                code(props) {
                  return <CodeRenderer value={props.children as string}/>
                }
              }}>{this._diagnosis.content}</ReactMarkdown>
            ) : (
              <p>This might take a few seconds.</p>
            )}
          </div>
          <p>
            <Button
              type="secondary"
              medium
              onClick={() => window.supportChat.sendMessage("My Private API is down oh no!")}
            >
              <span className="icon-speech-bubble" /> Feel free to contact us and we'll help you out
            </Button>
          </p>
        </div>
      </div>
    );
  }
}

const PageWithRouter = withRouter(NoLocalApiPage) as React.ComponentType<NoLocalApiPageProps>;
export { PageWithRouter as NoLocalApiPage };

function CodeRenderer({ value }: { value: string }) {
  return (
    <React.Fragment>
      <div className="copy-button-container">
        <CopyButton value={value} />
      </div>
      <pre>
        <code>
          {value.split("\n").map((text, index) => (
            <div key={index}>
              {text}
              {"\n"}
            </div>
          ))}
        </code>
      </pre>
    </React.Fragment>
  );
}
